import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgbTabset } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { forkJoin, Subscription } from 'rxjs';
import { finalize, map, take, tap } from 'rxjs/operators';
import { utils, WorkBook, WorkSheet, writeFile } from 'xlsx';

import { Util } from '../../common/common.util';
import { ProfileUserService } from '../../user/profile.user.service';
import { TranslationKeyMap } from '../reports.i18n';
import { ReportsService } from '../reports.service';
import { toCamel } from '../td-daily-huddle/td-daily-huddle.reports.component';

import {
    TdOffersReportPopulationType,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TdOffersReportResultParams,
    TdOffersReportSelectionParams,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    TdOffersResult,
    TdOffersResultRow,
    TdOffersUser,
} from './td-offers.reports.model';

enum Tabs {
    PARAMS = 'w-tab-report-0',
    GRID = 'w-tab-report-1',
}

@Component({
    selector: '[tdOffersReport]',
    templateUrl: './td-offers.reports.component.html',
    host: {
        class: 'cmps-reports-td-offers',
    },
})
export class TdOffersReportComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild('tabSet', { static: true }) tabSet: NgbTabset;
    @ViewChild('table') table: ElementRef<HTMLTableElement>;
    i18n = TranslationKeyMap;
    // pendingData: boolean;
    tooltipText: string;
    currDate = new Date();
    reportId: number;
    selectedParams: TdOffersReportSelectionParams = {
        startDate: Util.dateToNgbDate(
            new Date(this.currDate.getFullYear(), this.currDate.getMonth(), 1),
        ),
        endDate: Util.dateToNgbDate(this.currDate),
        populationType: TdOffersReportPopulationType.Individual,
        populationID: {
            userName: '',
            userId: 0,
        },
        defaultPopulationID: {
            userName: '',
            userId: 0,
        },
        invalidUser: false,
        invalidEndDate: false,
        invalidStartDate: false,
        pendingData: false,
        noData: true,
        isAsc: true,
    };

    sub: Subscription;
    selectedPopulation: string;
    selectedPopulationType: string;
    selectedStartDate: string;
    selectedEndDate: string;
    generatedDate: Date;
    superGroups: IOption[];
    data: TdOffersResultRow[];
    reportPopulations: IOption[] = [
        {
            label: 'SuperGroup',
            value: TdOffersReportPopulationType.SuperGroup,
        },
        {
            label: 'Individual',
            value: TdOffersReportPopulationType.Individual,
        },
    ];

    constructor(
        private readonly _reportService: ReportsService,
        private readonly _profileUserService: ProfileUserService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.selectedParams.pendingData = true;
        forkJoin([
            this._profileUserService.listSuperGroups().pipe(
                take(1),
                map((superGroups) =>
                    superGroups
                        .filter((s) => s.enabled)
                        .map((s) => ({
                            value: s.id.toString(),
                            label: s.name,
                        })),
                ),
                tap((superGroups) => (this.superGroups = superGroups)),
            ),
            this._reportService.retrieveDefaultHierarchyUser().pipe(
                take(1),
                tap((user: TdOffersUser) => {
                    this.selectedParams = {
                        ...this.selectedParams,
                        populationID: user,
                        defaultPopulationID: user,
                    };
                }),
            ),
        ])
            .pipe(
                finalize(() => {
                    this.selectedParams.pendingData = false;
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        this.sub = this.tabSet.tabChange
            .pipe(
                tap((t) => {
                    if (t.nextId === Tabs.GRID) {
                        this.getReportResult();
                    }
                }),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getReportResult() {
        if (!this.reportId) {
            this.selectedParams.noData = true;
            return;
        }

        this._reportService
            .retrieveGenericReportResult(this.reportId)
            .pipe(
                take(1),
                tap((result) => {
                    this.getSelectedPopulation();
                    const data = toCamel<TdOffersResultRow>(
                        JSON.parse(result.resultSet),
                    );
                    if (Array.isArray(data)) {
                        this.data = this.processDataArray(data);
                        this.selectedParams.noData = false;
                    } else {
                        this.selectedParams.noData = true;
                    }
                }),
                finalize(() => (this.selectedParams.pendingData = false)),
            )
            .subscribe();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    sortTable(columnNumber: Number) {
        let sorted: TdOffersResultRow[];
        switch (columnNumber) {
            case 0:
                if (Array.isArray(this.data)) {
                    if (this.selectedParams.isAsc) {
                        sorted = this.data.sort((a, b) =>
                            String(a.product).toLowerCase() >
                            String(b.product).toLowerCase()
                                ? 1
                                : -1,
                        );
                    } else {
                        sorted = this.data.sort((a, b) =>
                            String(b.product).toLowerCase() >
                            String(a.product).toLowerCase()
                                ? 1
                                : -1,
                        );
                    }
                    this.data = sorted;
                }
                break;
            case 1:
                if (Array.isArray(this.data)) {
                    if (this.selectedParams.isAsc) {
                        sorted = this.data.sort((a, b) =>
                            Number(a.totalOffer) > Number(b.totalOffer)
                                ? 1
                                : -1,
                        );
                    } else {
                        sorted = this.data.sort((a, b) =>
                            Number(b.totalOffer) > Number(a.totalOffer)
                                ? 1
                                : -1,
                        );
                    }
                    this.data = sorted;
                }
                break;
            case 2:
                if (Array.isArray(this.data)) {
                    if (this.selectedParams.isAsc) {
                        sorted = this.data.sort((a, b) =>
                            Number(a.totalYes) > Number(b.totalYes) ? 1 : -1,
                        );
                    } else {
                        sorted = this.data.sort((a, b) =>
                            Number(b.totalYes) > Number(a.totalYes) ? 1 : -1,
                        );
                    }
                    this.data = sorted;
                }
                break;
            case 3:
                if (Array.isArray(this.data)) {
                    if (this.selectedParams.isAsc) {
                        sorted = this.data.sort((a, b) =>
                            Number(a.totalNo) > Number(b.totalNo) ? 1 : -1,
                        );
                    } else {
                        sorted = this.data.sort((a, b) =>
                            Number(b.totalNo) > Number(a.totalNo) ? 1 : -1,
                        );
                    }
                    this.data = sorted;
                }
                break;
            case 4:
                if (Array.isArray(this.data)) {
                    if (this.selectedParams.isAsc) {
                        sorted = this.data.sort((a, b) =>
                            Number(a.totalMaybe) > Number(b.totalMaybe)
                                ? 1
                                : -1,
                        );
                    } else {
                        sorted = this.data.sort((a, b) =>
                            Number(b.totalMaybe) > Number(a.totalMaybe)
                                ? 1
                                : -1,
                        );
                    }
                    this.data = sorted;
                }
                break;
            case 5:
                if (Array.isArray(this.data)) {
                    if (this.selectedParams.isAsc) {
                        sorted = this.data.sort((a, b) =>
                            Number(a.offerActionRate) >
                            Number(b.offerActionRate)
                                ? 1
                                : -1,
                        );
                    } else {
                        sorted = this.data.sort((a, b) =>
                            Number(b.offerActionRate) >
                            Number(a.offerActionRate)
                                ? 1
                                : -1,
                        );
                    }
                    this.data = sorted;
                }
                break;
            case 6:
                if (Array.isArray(this.data)) {
                    if (this.selectedParams.isAsc) {
                        sorted = this.data.sort((a, b) =>
                            Number(a.conversion) > Number(b.conversion)
                                ? 1
                                : -1,
                        );
                    } else {
                        sorted = this.data.sort((a, b) =>
                            Number(b.conversion) > Number(a.conversion)
                                ? 1
                                : -1,
                        );
                    }
                    this.data = sorted;
                }
                break;
            default:
                break;
        }
        const overallRow = this.data.filter((o) => {
            return o.product === 'Overall';
        });

        if (overallRow.length > 0) {
            const overall = overallRow[0];
            const index = this.data.indexOf(overall);
            this.data.splice(index, 1);
            this.data.push(overall);
        }

        this.selectedParams.isAsc = !this.selectedParams.isAsc;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getSelectedPopulation() {
        this.selectedPopulationType = this.reportPopulations.find(
            (p) => p.value === this.selectedParams.populationType,
        ).label;

        switch (this.selectedParams.populationType) {
            case TdOffersReportPopulationType.SuperGroup:
                // eslint-disable-next-line no-case-declarations
                const superGroup = this.superGroups.find(
                    (s) => s.value === this.selectedParams.populationID,
                );
                this.selectedPopulation = superGroup ? superGroup.label : '';
                break;
            case TdOffersReportPopulationType.Individual:
                // eslint-disable-next-line no-case-declarations, @typescript-eslint/no-unnecessary-type-assertion
                const individual = this.selectedParams
                    .populationID as TdOffersUser;
                this.selectedPopulation = individual ? individual.userName : '';
                break;
            default:
                break;
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onGeneratedReport(reportId: number) {
        this.selectedStartDate =
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            this.selectedParams.startDate.year +
            '-' +
            this.selectedParams.startDate.month +
            '-' +
            this.selectedParams.startDate.day;
        this.selectedEndDate =
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            this.selectedParams.endDate.year +
            '-' +
            this.selectedParams.endDate.month +
            '-' +
            this.selectedParams.endDate.day;
        this.generatedDate = new Date();
        this.reportId = reportId;
        this.tabSet.select('w-tab-report-1');
    }

    processDataArray(dataArray: TdOffersResultRow[]): TdOffersResultRow[] {
        dataArray.forEach((el: TdOffersResultRow) => {
            console.log(el);
            el.totalOffer =
                String(el.totalOffer) === '-100'
                    ? '-'
                    : String(Number(el.totalOffer).toFixed(2));
            el.totalYes =
                String(el.totalYes) === '-100'
                    ? '-'
                    : String(Number(el.totalYes).toFixed(2));
            el.totalNo =
                String(el.totalNo) === '-100'
                    ? '-'
                    : String(Number(el.totalNo).toFixed(2));
            el.totalMaybe =
                String(el.totalMaybe) === '-100'
                    ? '-'
                    : String(Number(el.totalMaybe).toFixed(2));
            el.offerActionRate =
                String(el.offerActionRate) === '-100'
                    ? '-'
                    : `${String(Number(el.offerActionRate).toFixed(2))}`;
            el.conversion = Number.isNaN(
                Number(el.totalYes) / Number(el.totalOffer),
            )
                ? '-'
                : String(
                      (Number(el.totalYes) / Number(el.totalOffer)).toFixed(2),
                  );
        });
        return dataArray;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onExportClick() {
        const workSheet: WorkSheet = utils.table_to_sheet(
            this.table.nativeElement,
        );
        const workBook: WorkBook = utils.book_new();
        utils.book_append_sheet(workBook, workSheet, 'Sheet1');
        writeFile(workBook, 'Report Offer.xlsx', {
            bookType: 'xlsx',
            type: 'buffer',
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    getTooltip(tooltipText) {
        this.tooltipText = tooltipText;
    }
}
